import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface NetworkData {
  amount: number;
  apr: number;
}

const ActivityCard: React.FC = () => {
  const [stakingData, setStakingData] = useState<{
    totalStaked: number;
    networks: Record<string, NetworkData>;
  }>({
    totalStaked: 0,
    networks: {
      ethereum: { amount: 0, apr: 0 },
      solana: { amount: 0, apr: 0 },
      bnb: { amount: 0, apr: 0 },
    },
  });

  const [withdrawals, setWithdrawals] = useState<string[]>([]);
  const [stakingActivities, setStakingActivities] = useState<string[]>([]);

  useEffect(() => {
    const storedStakingData = localStorage.getItem('stakingData');
    const hour = new Date().getHours();
    const multiplier = 1 + (hour % 6) * 0.05;

    const generateDeterministicAPR = (network: string) => {
      const base = 40;
      const range = 170;
      const seed = hashString(network + hour);
      const normalized = seed % 1000 / 1000;
      return Math.round(base + normalized * range);
    };

    const hashString = (str: string) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      return Math.abs(hash);
    };

    if (storedStakingData) {
      setStakingData(JSON.parse(storedStakingData));
    } else {
      const networkBase = {
        ethereum: { amount: 117 * multiplier, apr: generateDeterministicAPR('ethereum') },
        solana: { amount: 2100 * multiplier, apr: generateDeterministicAPR('solana') },
        bnb: { amount: 1400 * multiplier, apr: generateDeterministicAPR('bnb') },
      };

      const totalStaked = Object.values(networkBase).reduce((sum, d) => sum + d.amount, 0);
      const initialStakingData = { totalStaked, networks: networkBase };
      setStakingData(initialStakingData);
      localStorage.setItem('stakingData', JSON.stringify(initialStakingData));
    }

    const withdrawalInterval = setInterval(() => {
      const randomNetwork = pickRandom(['solana', 'ethereum', 'bnb']);
      const amount = randomAmount(1, 10);
      const wallet = getWalletAddress(randomNetwork);
      const timestamp = getTimestamp();
      setWithdrawals(prev => [
        `${wallet} withdrew ${amount.toFixed(2)} ${getSymbol(randomNetwork)} at ${timestamp}.`,
        ...prev.slice(0, 19),
      ]);
    }, randomDelay());

    const stakingInterval = setInterval(() => {
      const randomNetwork = pickRandom(['solana', 'ethereum', 'bnb']);
      const amount = Math.floor(Math.random() * 20 + 0.1);
      const wallet = getWalletAddress(randomNetwork);
      const timestamp = getTimestamp();

      setStakingData(prev => {
        const newAmount = prev.networks[randomNetwork].amount + amount;
        const updatedNetworks = {
          ...prev.networks,
          [randomNetwork]: { ...prev.networks[randomNetwork], amount: newAmount },
        };
        const updatedTotal = Object.values(updatedNetworks).reduce((sum, d) => sum + d.amount, 0);
        const updated = { totalStaked: updatedTotal, networks: updatedNetworks };
        localStorage.setItem('stakingData', JSON.stringify(updated));
        return updated;
      });

      setStakingActivities(prev => [
        `${wallet} staked ${amount} ${getSymbol(randomNetwork)} at ${timestamp}.`,
        ...prev.slice(0, 19),
      ]);
    }, randomDelay());

    return () => {
      clearInterval(withdrawalInterval);
      clearInterval(stakingInterval);
    };
  }, []);

  const pickRandom = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)];
  const randomAmount = (min: number, max: number) => Math.random() * (max - min) + min;
  const randomDelay = () => Math.floor(Math.random() * (120000 - 10000) + 10000);
  const getTimestamp = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const getWalletAddress = (network: string) =>
    network === 'solana'
      ? `S${Math.random().toString(36).substr(2, 6)}...${Math.random().toString(36).substr(2, 4)}`
      : `0x${Math.random().toString(16).substr(2, 6)}...${Math.random().toString(16).substr(2, 4)}`;
  const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
  const getSymbol = (network: string) => (network === 'solana' ? 'SOL' : network === 'bnb' ? 'BNB' : 'ETH');
  const formatNumber = (num: number) =>
    num >= 1000 ? num.toLocaleString(undefined, { maximumFractionDigits: 2 }) : num.toFixed(2);

  return (
    <div className="w-full max-w-5xl bg-gradient-to-br from-[#050505] via-[#0a0a0a] to-[#0f0f0f] rounded-2xl shadow-2xl border border-pink-600 p-4 sm:p-6">
      <div className="text-center text-lg sm:text-xl font-mono text-pink-400 py-2 sm:py-4 border-b border-pink-700 uppercase tracking-widest glow">
        Live Activity
      </div>
      <div className="space-y-6 text-sm font-mono text-gray-300">
        {/* Staking Information */}
        <div>
          <h3 className="text-green-400 text-lg mb-2 py-2">Staking Information</h3>
          {Object.entries(stakingData.networks).map(([network, data]) => (
            <div key={network} className="flex justify-between py-1 hover:bg-gray-700 px-2 rounded">
              <span className="text-cyan-400">{capitalize(network)}</span>
              <span>
                {formatNumber(data.amount)} {getSymbol(network)}{' '}
                <span className="text-yellow-400 ml-2">APR: {data.apr}%</span>
              </span>
            </div>
          ))}
        </div>

        {/* Withdrawals */}
        <div>
          <h3 className="text-pink-400 text-lg mb-2">Withdrawal Activity</h3>
          <div className="max-h-24 overflow-y-auto scrollbar-thin scrollbar-thumb-pink-500 scrollbar-track-gray-800 space-y-2">
            <AnimatePresence initial={false}>
              {withdrawals.length > 0 ? (
                withdrawals.map((w, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.3 }}
                    className="text-gray-400 hover:text-gray-200 cursor-pointer"
                    title={w}
                  >
                    {w}
                  </motion.div>
                ))
              ) : (
                <div className="text-gray-500">No withdrawals yet.</div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Staking Activity */}
        <div>
          <h3 className="text-pink-400 text-lg mb-2">Staking Activity</h3>
          <div className="max-h-24 overflow-y-auto scrollbar-thin scrollbar-thumb-pink-500 scrollbar-track-gray-800 space-y-2">
            <AnimatePresence initial={false}>
              {stakingActivities.length > 0 ? (
                stakingActivities.map((a, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.3 }}
                    className="text-gray-400 hover:text-gray-200 cursor-pointer"
                    title={a}
                  >
                    {a}
                  </motion.div>
                ))
              ) : (
                <div className="text-gray-500">No staking yet.</div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityCard;
